import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bell,
  BellRing,
  BookOpen,
  Briefcase,
  Building2,
  CornerDownLeft,
  FileQuestion,
  FileText,
  LayoutDashboard,
  LoaderCircle,
  Plus,
  Rocket,
  Search,
  User,
  UserCircle,
  UserCog,
  Wrench,
  X,
} from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { getCachedSearch, globalSearch, normalizeQuery, SEARCH_MIN_LENGTH } from '../../services/searchService';
import {
  buildResultGroups,
  clearRecent,
  matchPages,
  readRecent,
  rememberRecent,
  splitMatches,
} from './searchResults';

// Global search (FECOA-2184): Cmd/Ctrl+K, or the button in the top bar, opens
// one box that finds pages and every study, site, participant, caregiver, DCQ
// and assessment the signed-in user can open.

export const OPEN_SEARCH_EVENT = 'flexcoa:open-search';
const DEBOUNCE_MS = 150;
const PAGE_MATCH_LIMIT = 4;

const TYPE_ICONS = {
  study: LayoutDashboard,
  site: Briefcase,
  participant: User,
  caregiver: UserCog,
  dcq: FileQuestion,
  assessment: FileText,
  study_draft: BookOpen,
};

const isMacLike = () => {
  try {
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
    return /mac|iphone|ipad|ipod/i.test(platform);
  } catch {
    return false;
  }
};

// Pages offered in the box follow the same rules as the sidebar.
const buildPages = ({ user, userPermissions, isDev }) => {
  const internal = !!user?.is_internal;
  const permissions = userPermissions || {};
  const canSelectSponsor =
    (internal && permissions.can_select_sponsor && permissions.sponsor_count > 1) || !!user?.is_superuser;
  return [
    { key: 'page:studies', title: 'Studies', url: '/studies', icon: LayoutDashboard, keywords: 'study home dashboard' },
    internal && permissions.can_access_study_builder && {
      key: 'page:studybuilder', title: 'Study Builder', url: '/studybuilder', icon: BookOpen, keywords: 'drafts design schedule',
    },
    internal && permissions.can_access_study_builder && {
      key: 'page:create-study', title: 'Create Study', url: '/studybuilder/create', icon: Plus, keywords: 'new study draft builder',
    },
    internal && permissions.can_access_assessment_builder && {
      key: 'page:assessment-builder', title: 'Assessment Builder', url: '/assessment-builder', icon: FileText, keywords: 'library questionnaire survey forms',
    },
    internal && permissions.can_promote_study_build && {
      key: 'page:study-promotion', title: 'Study Promotion', url: '/study-promotion', icon: Rocket, keywords: 'promote export import candidate',
    },
    { key: 'page:notifications', title: 'Notifications', url: '/notifications', icon: Bell, keywords: 'alerts inbox' },
    { key: 'page:profile', title: 'Profile', url: '/profile', icon: UserCircle, keywords: 'account password mfa security' },
    {
      key: 'page:notification-settings', title: 'Notification settings', url: '/profile?tab=notifications', icon: BellRing, keywords: 'preferences email alerts',
    },
    canSelectSponsor && {
      key: 'page:sponsor', title: 'Select Sponsor', url: '/sponsor-selector', state: { from: 'mainMenu' }, icon: Building2, keywords: 'switch sponsor',
    },
    isDev && { key: 'page:development', title: 'Development', url: '/development', icon: Wrench, keywords: 'tools debug' },
  ]
    .filter(Boolean)
    .map((page) => ({ ...page, type: 'page' }));
};

const Highlight = ({ text, query }) => {
  if (!query) return text;
  return splitMatches(text, query).map((part, index) =>
    part.match ? (
      <mark key={index} className="rounded-[3px] bg-primary-100/80 text-inherit dark:bg-primary-500/25">
        {part.text}
      </mark>
    ) : (
      <React.Fragment key={index}>{part.text}</React.Fragment>
    ),
  );
};

const Kbd = ({ children, className = '' }) => (
  <kbd
    className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded border border-gray-200 bg-white px-1 font-sans text-[11px] font-medium text-gray-500 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-400 ${className}`}
  >
    {children}
  </kbd>
);

const SearchDialog = ({ onClose, pages, userKey }) => {
  const navigate = useNavigate();
  const baseId = useId();
  const listId = `${baseId}-list`;
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('idle');
  const [response, setResponse] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState(() => readRecent(userKey));
  // Keyboard moves scroll the list; mouse moves must not fight that.
  const lastPointer = useRef(null);

  const q = normalizeQuery(query);
  const searchable = q.length >= SEARCH_MIN_LENGTH;

  useEffect(() => {
    if (!searchable) {
      setStatus('idle');
      setResponse(null);
      return undefined;
    }
    const cached = getCachedSearch(q);
    if (cached) {
      setResponse({ query: q, data: cached });
      setStatus('done');
      return undefined;
    }
    setStatus('loading');
    const controller = new AbortController();
    const timer = setTimeout(() => {
      globalSearch(q, { signal: controller.signal })
        .then((data) => {
          if (controller.signal.aborted) return;
          setResponse({ query: q, data });
          setStatus('done');
        })
        .catch(() => {
          if (!controller.signal.aborted) setStatus('error');
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [q, searchable]);

  const sections = useMemo(() => {
    if (!q) {
      return [
        recent.length > 0 && { key: 'recent', label: 'Recent', items: recent, clearable: true },
        { key: 'pages', label: 'Go to', items: pages },
      ].filter(Boolean);
    }
    const matchedPages = matchPages(pages, q).slice(0, PAGE_MATCH_LIMIT);
    const groups = searchable && response ? buildResultGroups(response.data) : [];
    // Records first: someone typing a study name wants the study, not the Studies page.
    return [...groups, matchedPages.length > 0 && { key: 'pages', label: 'Pages', items: matchedPages }].filter(Boolean);
  }, [q, searchable, response, pages, recent]);

  const flatItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);

  useEffect(() => {
    setActiveIndex(0);
  }, [q, response]);

  useEffect(() => {
    if (activeIndex >= flatItems.length) setActiveIndex(flatItems.length ? flatItems.length - 1 : 0);
  }, [activeIndex, flatItems.length]);

  // Keep the highlighted row in view while moving with the keyboard.
  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const openItem = useCallback(
    (item, event) => {
      if (!item) return;
      rememberRecent(userKey, item);
      const newTab = event && (event.metaKey || event.ctrlKey || event.button === 1);
      if (newTab) {
        window.open(item.url, '_blank', 'noopener');
        return;
      }
      onClose();
      navigate(item.url, item.state ? { state: item.state } : undefined);
    },
    [navigate, onClose, userKey],
  );

  const onInputKeyDown = (event) => {
    if (event.nativeEvent?.isComposing) return;
    const count = flatItems.length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (count) setActiveIndex((index) => (index + 1) % count);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (count) setActiveIndex((index) => (index - 1 + count) % count);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      openItem(flatItems[activeIndex], event);
    }
  };

  // Escape closes; Tab stays inside the box.
  const onPanelKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key === 'Tab') {
      const focusable = Array.from(panelRef.current?.querySelectorAll('input, button') || []).filter((el) => !el.disabled);
      if (!focusable.length) return;
      const index = focusable.indexOf(document.activeElement);
      const next = event.shiftKey ? (index <= 0 ? focusable.length - 1 : index - 1) : (index + 1) % focusable.length;
      event.preventDefault();
      focusable[next].focus();
    }
  };

  const clearRecentItems = () => {
    clearRecent(userKey);
    setRecent([]);
    inputRef.current?.focus();
  };

  const activeItem = flatItems[activeIndex];
  const activeId = activeItem ? `${baseId}-option-${activeIndex}` : undefined;
  const loading = status === 'loading';
  const hasServerResults = sections.some((section) => section.key !== 'pages' && section.key !== 'recent');
  const showNoResults = searchable && status === 'done' && flatItems.length === 0;
  const showSearchingRow = searchable && loading && !hasServerResults;

  let runningIndex = -1;

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[12vh] sm:px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
    >
      <div className="absolute inset-0 bg-gray-950/40 dark:bg-black/70" aria-hidden="true" onMouseDown={onClose} />
      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onKeyDown={onPanelKeyDown}
        className="relative flex max-h-[min(70vh,560px)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 dark:border-white/[0.06]">
          {loading ? (
            <LoaderCircle className="h-[18px] w-[18px] flex-shrink-0 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          ) : (
            <Search className="h-[18px] w-[18px] flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          )}
          <input
            ref={inputRef}
            autoFocus
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            aria-label="Search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search studies, sites, participants, assessments..."
            className="h-14 min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-gray-50 dark:placeholder:text-gray-500"
            // The global dark rule for inputs paints a field background; this one sits on the panel.
            style={{ backgroundColor: 'transparent' }}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="hidden flex-shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 sm:block"
            aria-label="Close search"
          >
            <Kbd>Esc</Kbd>
          </button>
        </div>

        <div ref={listRef} id={listId} role="listbox" aria-label="Search results" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-2">
          {showSearchingRow && (
            <div className="space-y-1 px-2.5 pt-3" aria-hidden="true">
              {[0, 1, 2].map((row) => (
                <div key={row} className="flex items-center gap-3 py-1.5">
                  <span className="h-8 w-8 animate-skeleton rounded-lg bg-gray-100/80 dark:bg-white/[0.06]" />
                  <span className="flex-1 space-y-1.5">
                    <span className="block h-3 w-1/3 animate-skeleton rounded bg-gray-100/80 dark:bg-white/[0.06]" />
                    <span className="block h-2.5 w-1/2 animate-skeleton rounded bg-gray-100/80 dark:bg-white/[0.06]" />
                  </span>
                </div>
              ))}
            </div>
          )}

          {sections.map((section) => {
            const headingId = `${baseId}-group-${section.key}`;
            return (
              <div key={section.key} role="group" aria-labelledby={headingId} className="pt-2">
                <div className="flex items-center justify-between px-2.5 pb-1 pt-1.5">
                  <span id={headingId} className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {section.label}
                  </span>
                  {section.clearable && (
                    <button
                      type="button"
                      onClick={clearRecentItems}
                      className="rounded text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {section.items.map((item) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  const active = index === activeIndex;
                  const Icon = item.icon || TYPE_ICONS[item.type] || FileText;
                  return (
                    <div
                      key={`${section.key}-${item.key}`}
                      id={`${baseId}-option-${index}`}
                      data-index={index}
                      role="option"
                      aria-selected={active}
                      onMouseMove={(event) => {
                        const point = `${event.clientX},${event.clientY}`;
                        if (lastPointer.current === point) return;
                        lastPointer.current = point;
                        if (!active) setActiveIndex(index);
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => openItem(item, event)}
                      onAuxClick={(event) => {
                        if (event.button === 1) openItem(item, event);
                      }}
                      className={`flex cursor-pointer select-none items-center gap-3 rounded-lg px-2.5 py-2 transition-colors ${
                        active ? 'bg-gray-100/80 dark:bg-white/[0.07]' : ''
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ring-1 transition-colors ${
                          active
                            ? 'bg-white text-primary-600 ring-gray-200 dark:bg-gray-900 dark:text-primary-400 dark:ring-white/10'
                            : 'bg-gray-50 text-gray-500 ring-gray-200/80 dark:bg-white/[0.04] dark:text-gray-400 dark:ring-white/[0.06]'
                        }`}
                        aria-hidden="true"
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                          <Highlight text={item.title} query={q} />
                        </span>
                        {item.subtitle && (
                          <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                            <Highlight text={item.subtitle} query={q} />
                          </span>
                        )}
                      </span>
                      {active && <CornerDownLeft className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {q && !searchable && (
            <p className="px-2.5 pb-2 pt-4 text-sm text-gray-500 dark:text-gray-400">
              Keep typing to search studies, sites, participants and more.
            </p>
          )}

          {showNoResults && (
            <div className="px-6 py-10 text-center">
              <Search className="mx-auto h-6 w-6 text-gray-300 dark:text-gray-600" aria-hidden="true" />
              <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100">No results for &ldquo;{q}&rdquo;</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try a study code, site name, participant number or assessment name.
              </p>
            </div>
          )}

          {status === 'error' && (
            <p className="px-2.5 pb-2 pt-4 text-sm text-danger-600 dark:text-danger-400" role="alert">
              Search is not available right now. Please try again in a moment.
            </p>
          )}
        </div>

        <div className="hidden items-center gap-4 border-t border-gray-100 bg-gray-50/70 px-4 py-2.5 text-xs text-gray-500 dark:border-white/[0.06] dark:bg-gray-900/40 dark:text-gray-400 sm:flex">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            to move
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd>
            to open
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>{isMacLike() ? '⌘' : 'Ctrl'}</Kbd>
            <Kbd>↵</Kbd>
            new tab
          </span>
        </div>

        <div className="sr-only" role="status" aria-live="polite">
          {loading ? 'Searching' : showNoResults ? `No results for ${q}` : searchable && status === 'done' ? `${flatItems.length} results` : ''}
        </div>
      </motion.div>
    </motion.div>
  );
};

const GlobalSearch = ({ isDev = false }) => {
  const { user, userPermissions } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const mac = useMemo(() => isMacLike(), []);
  const userKey = user?.id ?? user?.email ?? '';
  const pages = useMemo(() => buildPages({ user, userPermissions, isDev }), [user, userPermissions, isDev]);

  const openSearch = useCallback(() => {
    restoreFocusRef.current = document.activeElement;
    setOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setOpen(false);
    const target = restoreFocusRef.current;
    restoreFocusRef.current = null;
    // Give focus back to where the person was, after the box has gone.
    window.setTimeout(() => {
      if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
    }, 0);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented) return;
      const key = (event.key || '').toLowerCase();
      if (key === 'k' && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey) {
        // Rich text editors use Cmd/Ctrl+K for links.
        const target = event.target;
        if (target?.isContentEditable || target?.closest?.('[contenteditable=""], [contenteditable="true"]')) return;
        event.preventDefault();
        if (open) closeSearch();
        else openSearch();
      }
    };
    const onOpenRequest = () => openSearch();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(OPEN_SEARCH_EVENT, onOpenRequest);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(OPEN_SEARCH_EVENT, onOpenRequest);
    };
  }, [open, openSearch, closeSearch]);

  // Stop the page behind from scrolling while the box is open.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openSearch}
        aria-label="Search"
        aria-haspopup="dialog"
        aria-keyshortcuts={mac ? 'Meta+K' : 'Control+K'}
        className="flex h-9 w-9 items-center justify-center gap-2 rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white sm:w-56 sm:justify-start sm:border sm:border-gray-200 sm:bg-gray-50 sm:px-2.5 sm:text-gray-500 sm:hover:border-gray-300 sm:hover:bg-white sm:hover:text-gray-700 lg:w-64 dark:sm:border-white/10 dark:sm:bg-white/[0.04] dark:sm:text-gray-400 dark:sm:hover:bg-white/[0.07] dark:sm:hover:text-gray-200"
      >
        <Search className="h-[18px] w-[18px] flex-shrink-0 sm:h-4 sm:w-4" strokeWidth={1.75} aria-hidden="true" />
        <span className="hidden flex-1 truncate text-left text-sm sm:block">Search</span>
        <span className="hidden items-center gap-0.5 sm:flex" aria-hidden="true">
          <Kbd>{mac ? '⌘' : 'Ctrl'}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      {createPortal(
        <AnimatePresence>{open && <SearchDialog key="global-search" onClose={closeSearch} pages={pages} userKey={userKey} />}</AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default GlobalSearch;
